import React, { useEffect, useState, useMemo } from 'react';
import { Button, Dropdown, Row, Spinner, Form } from 'react-bootstrap';
import { defaultTheme, useTheme, useFlowPortalContent } from '../../context/PortalThemeContext';
import { useRouteContext } from '../../context/RouteContext';
import '../returns/OrderSummary.css';
import '../returns/ReturnConfirmationPage.css';
import '../returns/NavButtons.css';
import './ClaimsConfirmationPage.css';
import { ClaimStates } from './ClaimStates';
import { getDiscountedPrice, ItemCard, VariantCard } from '../shared/ProductVariantCards';
import InfoCard from '../shared/InfoCard';
import EditAddressModal from '../shared/EditAddressModal';
import { fetchDynamicOrder } from '../../utils/orderLookupApi';
import { fetchWithSessionToken } from '../shared/fetchWithSessionToken';
import { CHAT_API_BASE } from '../../config/api';
import { PiCaretDown, PiShield } from 'react-icons/pi';
import { fallbackString } from '../shared/FallbackUtils';
import ReturnPolicyLink from '../returns/ReturnPolicyLink';
import ModalOverlay from '../shared/ModalOverlay';
import mockOrderData from '../../mock/mockOrderData.json';
import { useMockStep } from '../../hooks/useMockStep';
import { getDiscountInfo } from '../../utils/discountUtils';
import SummaryItemRow from '../shared/SummaryItemRow';

// Claim enums to match backend model
const Claim = {
    ClaimStatus: {
        NONE: 'NONE',
        LOST: 'LOST',
        DAMAGED: 'DAMAGED',
        STOLEN: 'STOLEN'
    },
    ClaimReviewStatus: {
        NOT_REVIEWED: 'NOT_REVIEWED',
        IN_REVIEW: 'IN_REVIEW',
        APPROVED: 'APPROVED',
        DENIED: 'DENIED',
        REQUEST: 'REQUEST',
        RESOLVED: 'RESOLVED'
    }
};

const ClaimsConfirmationPage = ({
    orderDetails,
    updateOrderDetails,
    itemsToClaim,
    reasonWithResolution,
    customer,
    shopifyProducts,
    onStepChange,
    flowType = 'CLAIM'
}) => {
    const { setOrderDetails: setRouteOrderDetails } = useRouteContext();
    const theme = useTheme();
    const useMock = useMockStep();
    const defaultSettings = defaultTheme.flowPortalContentSettings.CLAIM.review;

    // Use the new flow-specific hook
    const flowPortalContent = useFlowPortalContent(flowType);
    const reviewSettings = flowPortalContent?.review || defaultSettings;

    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editMode, setEditMode] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitMessage, setSubmitMessage] = useState('');

    // Calculate total order value and discounts
    const totalOrderPrice = orderDetails?.line_items?.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0) ?? 0;
    const totalOrderDiscount = parseFloat(orderDetails?.current_total_discounts ?? 0);
    const actualOrderTotal = totalOrderPrice - totalOrderDiscount;

    // Create a map of item IDs to their actual prices (with discounts applied)
    const itemIdToActualPrice = useMemo(() => {
        const priceMap = new Map();
        itemsToClaim.forEach(item => {
            const orderLineItem = orderDetails.line_items.find(lineItem =>
                lineItem.id === item.id || lineItem.id === parseInt(item.id)
            );

            if (!orderLineItem) {
                // Fallback to original price if line item not found
                priceMap.set(item.id, parseFloat(item.price));
            } else if (orderLineItem.discount_allocations !== undefined && Array.isArray(orderLineItem.discount_allocations)) {
                // Use discount allocation calculation
                const discountInfo = getDiscountInfo(orderLineItem);
                priceMap.set(item.id, discountInfo.discountedPrice);
            } else {
                // Fallback to legacy logic
                const itemPrice = parseFloat(orderLineItem.price);
                const proportionalDiscount = totalOrderDiscount * (itemPrice / totalOrderPrice);
                priceMap.set(item.id, (itemPrice - proportionalDiscount));
            }
        });
        return priceMap;
    }, [itemsToClaim, orderDetails, totalOrderDiscount, totalOrderPrice]);

    // Calculate current claim items total (original prices)
    const currentClaimOriginalPriceTotal = itemsToClaim.reduce((sum, item) => sum + (parseFloat(item.price) * (item.claimQuantity || 1)), 0);

    // Calculate accurate claim amount using the price map
    const actualClaimAmount = itemsToClaim.reduce((sum, item) => {
        const actualPrice = (itemIdToActualPrice.get(item.id) * item.claimQuantity || 1) ?? 0;
        return sum + actualPrice;
    }, 0);

    // Calculate subtotal for line items (used in ItemCard)
    const subtotalLineItems = orderDetails?.line_items?.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0) ?? 0;

    const refreshOrderDetails = async (orderId, shopId, customerEmail, updateOrderDetails) => {
        try {
            const response = await fetchDynamicOrder({
                companyId: shopId,
                searchParam: orderId,
                additionalParam: customerEmail || orderDetails?.shipping_address?.zip || orderDetails?.shipping_address?.phone,
            });

            if (!response.ok) throw new Error('Failed to fetch order');

            const data = await response.json();

            const enrichedOrder = data.order;

            // Ensure we have all the necessary data
            if (!enrichedOrder.customer && Array.isArray(data.orders) && data.order?.customer) {
                enrichedOrder.customer = data.order.customer;
            }

            // Update order with all necessary data
            enrichedOrder.orderId = orderId;
            enrichedOrder.orderReturns = data.orderReturns;
            enrichedOrder.orderClaims = data.orderClaims;
            enrichedOrder.trackingInfo = data.trackingInfo;
            enrichedOrder.shopifyProducts = data.shopifyProducts;
            enrichedOrder.bearerToken = data.bearerToken;
            enrichedOrder.shopId = shopId;

            if (data.bearerToken && enrichedOrder.order_number) {
                localStorage.setItem('sessionToken', data.bearerToken);
                localStorage.setItem('orderId', enrichedOrder.order_number);
                localStorage.setItem('shopId', shopId);
                localStorage.setItem('userEmail', customerEmail);
            }

            localStorage.setItem('brella_order', JSON.stringify(enrichedOrder));
            updateOrderDetails(enrichedOrder);
        } catch (err) {
            console.error('Error refreshing order:', err);
        }
    };

    // Helper function to clean address objects for backend compatibility
    const cleanAddress = (address) => {
        if (!address) return null;

        return {
            address1: address.address1,
            address2: address.address2,
            city: address.city,
            country: address.country,
            first_name: address.first_name,
            last_name: address.last_name,
            province: address.province,
            zip: address.zip
        };
    };

    // Helper function to map user-friendly resolution names to backend enum values
    const mapResolutionToEnum = (resolutionName) => {
        if (!resolutionName) return 'NONE';

        const lowerName = resolutionName.toLowerCase();

        if (lowerName.includes('refund') || lowerName.includes('money back')) {
            return 'REFUND_FULL';
        } else if (lowerName.includes('replace') || lowerName.includes('reship')) {
            return 'RESHIP_ITEMS';
        } else if (lowerName.includes('store credit') || lowerName.includes('credit')) {
            return 'STORE_CREDIT';
        } else if (lowerName.includes('partial') || lowerName.includes('partial refund')) {
            return 'REFUND_PARTIAL';
        } else if (lowerName.includes('deny') || lowerName.includes('denied')) {
            return 'DENIED';
        } else if (lowerName.includes('request') || lowerName.includes('info')) {
            return 'REQUEST_INFO';
        } else {
            return 'NONE';
        }
    };

    const handleSubmitClaim = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            // Prepare the base claim data according to backend model
            const orderIdForClaim = orderDetails.confirmation_number || orderDetails.order_number || orderDetails.id;

            // Create a single claim with multiple claimed items
            const claimedItems = [];
            const allImages = [];
            let desc = `${reasonWithResolution.name}`;
            if (reasonWithResolution.optionalText) {
                desc += ` - ${reasonWithResolution.optionalText}`;
            }
            if (reasonWithResolution.resolution) {
                desc += ` | Resolution: ${reasonWithResolution.resolution}`;
            }

            itemsToClaim.forEach((item) => {

                // Create claimed item for this product
                const claimedItem = {
                    lineItemId: item.id.toString(),
                    productId: item.product_id?.toString() || item.variant_id?.toString(),
                    productName: item.name,
                    productSrc: item.image_src || null,
                    itemPrice: itemIdToActualPrice.get(item.id) || parseFloat(item.price) || 0,
                    quantity: item.claimQuantity || 1,
                    // itemDescription: desc,
                    itemClaimStatus: reasonWithResolution.claimStatus ?? 'DAMAGED' // Default status
                };

                claimedItems.push(claimedItem);

                // Collect images for this specific item
            });

            if (reasonWithResolution.images && reasonWithResolution.images.length > 0) {
                reasonWithResolution.images.forEach(image => {
                    allImages.push({
                        itemId: claimedItems.length > 0 ? claimedItems[0].lineItemId : 'claim',
                        itemName: claimedItems.length > 0 ? claimedItems[0].productName : 'evidence',
                        issueName: reasonWithResolution.name,
                        resolutionName: reasonWithResolution?.name || null,
                        imageName: image.name,
                        imageSize: image.size,
                        imageType: image.type,
                        image: image
                    });
                });
            }

            if (claimedItems.length === 0) {
                throw new Error('No valid items to claim');
            }

            // Create the main claim data with claimedItems list
            const claimData = {
                orderId: orderIdForClaim,
                confirmationNumber: orderDetails.confirmation_number,
                customerId: customer?.email,
                shopId: orderDetails.shopId,
                description: desc,
                claimStatus: reasonWithResolution.claimStatus ?? 'DAMAGED', // Default status
                reviewStatus: 'NOT_REVIEWED',
                imageUrls: [], // Empty array for claims without images
                returnFromAddress: cleanAddress(orderDetails.shipping_address),
                shipToAddress: cleanAddress(orderDetails.billing_address),
                claimedItems: claimedItems,
                customerChosenResolution: reasonWithResolution.name ? mapResolutionToEnum(reasonWithResolution.name) : 'NONE',
                createdAt: new Date(),
                updatedAt: new Date()
            };

            // Submit the single claim with all items
            let response;
            if (allImages.length > 0) {
                // Use multipart form data for claims with images
                const formData = new FormData();
                formData.append('claimData', JSON.stringify(claimData));

                // Add all images
                allImages.forEach((imageData, imageIndex) => {
                    const imageName = `${imageData.itemId}_${imageData.itemName.replace(/\s+/g, '_')}_${imageIndex}.${imageData.imageName.split('.').pop()}`;
                    formData.append('images', imageData.image, imageName);

                    // Also add metadata for each image
                    formData.append('imageMetadata', JSON.stringify({
                        itemId: imageData.itemId,
                        itemName: imageData.itemName,
                        issueName: imageData.issueName,
                        resolutionName: imageData.resolutionName,
                        originalName: imageData.imageName,
                        imageIndex: imageIndex
                    }));
                });

                response = await fetchWithSessionToken(`${CHAT_API_BASE}/companies/${orderDetails.shopId}/claims/with-images`, {
                    method: 'POST',
                    body: formData
                });
            } else {
                // Use JSON body for claims without images
                response = await fetchWithSessionToken(`${CHAT_API_BASE}/companies/${orderDetails.shopId}/claims/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(claimData),
                });
            }

            if (response.ok) {
                const savedClaim = await response.json();

                // Show success message
                setSubmitSuccess(true);
                setSubmitMessage(`Successfully submitted claim for ${claimedItems.length} item${claimedItems.length > 1 ? 's' : ''}!`);

                // Refresh order details to get updated claim information
                if (orderDetails.confirmation_number) {
                    await refreshOrderDetails(
                        orderDetails.confirmation_number,
                        orderDetails.shopId,
                        customer?.email,
                        updateOrderDetails
                    );
                }

                setTimeout(() => {
                    onStepChange(ClaimStates.CLAIM_SUBMITTED);
                }, 1500);
            } else {
                const errorData = await response.text();
                console.error('Claim submission failed with status:', response.status);
                console.error('Response headers:', Object.fromEntries(response.headers.entries()));
                console.error('Error response body:', errorData);

                if (response.status === 415) {
                    throw new Error(`Unsupported Media Type (415): The server cannot process the request format. This usually happens with image uploads. Error: ${errorData}`);
                } else {
                    throw new Error(`Failed to submit claim (${response.status}): ${errorData}`);
                }
            }



        } catch (error) {
            console.error('Error submitting claim:', error);
            setSubmitError('Failed to submit claim. Please try again.');

            // Hide error message after 5 seconds
            setTimeout(() => {
                setSubmitError(null);
            }, 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditClick = (type) => {
        setEditMode(type);
        setShowEditModal(true);
    };

    const handleAddressSave = (updatedAddress) => {
        updateOrderDetails(prev => ({
            ...prev,
            shipping_address: updatedAddress
        }));
        setShowEditModal(false);
    };

    const HeaderWithDetails = ({ headerText, detailTexts }) => (
        <div className='rc-header-detail-item'>
            <div>
                {headerText}
            </div>
            {(detailTexts ?? []).map((text, index) => (
                <div key={text} className='medium-text' style={{ gap: '0.5rem' }}>
                    <p key={index}>{text}</p>
                </div>
            ))}
        </div>
    );

    function getProduct(product_id) {
        return shopifyProducts?.find(product => product.id === product_id);
    }

    function getProductFromVariant(variant_id) {
        return shopifyProducts?.find(product =>
            product.variants?.some(variant => variant.id === variant_id)
        );
    }

    const AddressCard = ({ title, address, onEdit }) => (
        <div className="address-card">
            <div className="address-header">
                <h4>{title}</h4>
                <Button variant="link" onClick={onEdit} className="edit-button">
                    Edit
                </Button>
            </div>
            <div className="address-content">
                <p>{address.first_name} {address.last_name}</p>
                <p>{address.address1}</p>
                {address.address2 && <p>{address.address2}</p>}
                <p>{address.city}, {address.province} {address.zip}</p>
                <p>{address.country}</p>
            </div>
        </div>
    );

    const ImageViewerModal = ({ image, onClose }) => {
        if (!image) return null;

        return (
            <ModalOverlay onClose={onClose}>
                <div className="image-viewer-modal">
                    <img src={URL.createObjectURL(image)} alt="Full size" />
                    <Button variant="secondary" onClick={onClose}>
                        {fallbackString(reviewSettings?.closeText, defaultSettings.closeText)}
                    </Button>
                </div>
            </ModalOverlay>
        );
    };

    return (
        <>
        <div
            className="d-flex-column"
            style={{
                backgroundColor: theme.background,
                width: '100%',
                maxWidth: '60rem',
            }}
        >
            <h3 className="rc-title">
                {fallbackString(reviewSettings?.title, defaultSettings.title)}
            </h3>

            <HeaderWithDetails headerText={fallbackString(reviewSettings?.shippingInformationText, defaultSettings.shippingInformationText)} />

            <div
                className='d-flex-row'
                style={{
                    backgroundColor: theme.background,
                    color: theme.textDark,
                }}
            >
                {/* Column 1 - Shipping and item info */}
                <div style={{ flex: '2' }}>

                    <div className='d-flex-column flex-md-row' style={{ gap: '1.5rem', marginBottom: '1.5rem' }}>
                        <div style={{ flex: 1, width: '100%' }}>
                            <AddressCard
                                title={fallbackString(reviewSettings?.shippingAddressText, defaultSettings.shippingAddressText)}
                                address={orderDetails.shipping_address}
                                onEdit={() => handleEditClick('shipping')}
                            />
                        </div>
                    </div>

                    <HeaderWithDetails
                        headerText={fallbackString(reviewSettings?.headingTextReturning, defaultSettings.headingTextReturning)}
                        detailTexts={[
                            fallbackString(reviewSettings?.bodyTextReturning, defaultSettings.bodyTextReturning),
                            <span style={{ fontWeight: '600' }}>Claim review by {new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            })}.</span>,
                        ]}
                    />

                    <div
                        className='card'
                        style={{
                            width: '100%',
                            padding: '1rem',
                            marginTop: '.5rem',
                        }}
                    >
                        {itemsToClaim.map((item, index) => {
                            return (
                                <div key={index + item.id}>
                                    <ItemCard
                                        item={item}
                                        shopifyProduct={getProduct(item.product_id)}
                                        horizontal={true}
                                        cardStyle={false}
                                        orderDiscount={totalOrderDiscount}
                                        orderTotal={totalOrderPrice}
                                        flowType="CLAIM"
                                    />
                                </div>
                            );
                        })}
                        <div className='ms-4'>
                            {reasonWithResolution && (
                                <div className="pt-3 mb-2" style={{ borderTop: '1px solid var(--border-color)' }}>
                                    {/* Show the issue (first step) */}
                                    <p className='medium-text'>
                                        <span style={{ fontWeight: '600' }}>{fallbackString(reviewSettings?.issueLabel, defaultSettings.issueLabel)}: </span>
                                        {reasonWithResolution.name}
                                        {reasonWithResolution.optionalText && (
                                            <span> - {reasonWithResolution.optionalText}</span>
                                        )}
                                    </p>

                                    {/* Show the resolution (second step) if it exists */}
                                    {reasonWithResolution.resolution && (
                                        <p className='medium-text mt-1'>
                                            <span style={{ fontWeight: '600' }}>{fallbackString(reviewSettings?.resolutionLabel, defaultSettings.resolutionLabel)}: </span>
                                            {reasonWithResolution.resolution}
                                        </p>
                                    )}

                                    {/* Show images from the issue step (first step) */}
                                    {reasonWithResolution.images && reasonWithResolution.images.length > 0 && (
                                        <div className="return-images-grid mb-2" style={{ marginTop: '0.75rem' }}>
                                            {reasonWithResolution.images.map((image, imageIndex) => (
                                                <div
                                                    key={imageIndex}
                                                    className="return-image-container"
                                                    onClick={() => setSelectedImage(image)}
                                                >
                                                    <img
                                                        src={URL.createObjectURL(image)}
                                                        alt={`Claim image ${imageIndex + 1}`}
                                                        className="return-image"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Column 2 - Summary */}
                <div style={{ flex: '1', minWidth: '15rem' }}>
                    <div className="summary-card">
                        <h2 className="card-header-text" style={{ margin: 0 }}>
                            {fallbackString(reviewSettings?.summaryTitle, defaultSettings.summaryTitle)}
                        </h2>

                        <SummaryItemRow
                            name={fallbackString(reviewSettings?.itemsBeingClaimedText, defaultSettings.itemsBeingClaimedText)}
                            amount={currentClaimOriginalPriceTotal.toFixed(2)}
                        />
                        <SummaryItemRow
                            name={fallbackString(reviewSettings?.discountsAppliedText, defaultSettings.discountsAppliedText)}
                            amount={(currentClaimOriginalPriceTotal - actualClaimAmount).toFixed(2)}
                            sign="-"
                            className="text-muted"
                        />

                        <div className="divider-horizontal" />

                        <SummaryItemRow
                            name={fallbackString(reviewSettings?.estimatedClaimAmountText, defaultSettings.estimatedClaimAmountText)}
                            amount={actualClaimAmount.toFixed(2)}
                            bold={true}
                        />

                        <div style={{ fontSize: '0.85rem', color: theme.textLight, marginTop: '0.5rem' }}>
                            {fallbackString(reviewSettings?.finalAmountNote, defaultSettings.finalAmountNote)}
                        </div>
                    </div>
                    {submitError && (<div className="alert alert-danger mt-2">{submitError}</div>)}
                </div>
            </div>

            <ReturnPolicyLink flowType={flowType} />

            {/* Edit Address Modal */}
            {showEditModal && (
                <EditAddressModal
                    show={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    address={editMode === 'shipping' ? orderDetails.shipping_address : orderDetails.billing_address}
                    onSave={handleAddressSave}
                    titleText={editMode === 'shipping' ?
                        fallbackString(reviewSettings?.shippingAddressText, defaultSettings.shippingAddressText) :
                        fallbackString(reviewSettings?.billingAddressText, defaultSettings.billingAddressText)
                    }
                />
            )}

            {/* Image Viewer Modal */}
            {selectedImage && (
                <ImageViewerModal
                    image={selectedImage}
                    onClose={() => setSelectedImage(null)}
                />
            )}
        </div>

        <div className="sticky-bottom" style={{ marginTop: '2rem', zIndex: '100' }}>
            <nav className="navigation-buttons">
                <div
                    className="back-button"
                    onClick={() => onStepChange(ClaimStates.CHOOSE_ISSUE)}
                >
                    Back
                </div>
                <div onClick={() => {
                    if (!isSubmitting && !useMock) handleSubmitClaim();
                }}>
                    <Button
                        className="button-accent"
                        disabled={isSubmitting || useMock}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {fallbackString(reviewSettings?.submittingClaimText, defaultSettings.submittingClaimText)}
                            </>
                        ) : useMock ? (
                            fallbackString(reviewSettings?.disabledDuringDemoText, defaultSettings.disabledDuringDemoText)
                        ) : (
                            fallbackString(reviewSettings?.submitClaimText, defaultSettings.submitClaimText)
                        )}
                    </Button>
                </div>
            </nav>
        </div>
        </>
    );
};

export default ClaimsConfirmationPage;
